import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { OrganizationService } from '../../core/data-services/organization.service';
import { OrganizationOption } from '../../core/response-models/organization-option.model';
import { ChemicalReportService } from '../../core/api-services/chemical-report.service';
import { VOLUME_UNITS } from 'src/app/shared/components/constants/unit-options';
import { ChemAmountPipe, UnitSelection, convertVolume } from './chemical-amount.pipe';
import { ChemicalReportOrgDetail } from '../../core/response-models/chemical-report-org-detail.model';
import { ChemicalReportSummary } from '../../core/response-models/chemical-report-summary.model';
import { ChemicalReportAggregate } from '../../core/response-models/chemical-report-aggregate.model';
import { ChemicalReportFilterOptions } from '../../core/response-models/chemical-report-filter-options.model';
import { ChemicalOption } from '../../core/response-models/chemical-option.model';
import { openExternal } from '../../core/utils/external-link-utils';

/**
 * Chemical Reports Admin Page
 *
 * Displays chemical usage reports. Select "All Organizations" for a cross-org summary,
 * or a specific organization for detailed drill-downs.
 */
@Component({
  selector: 'app-chemical-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, ChemAmountPipe],
  templateUrl: './chemical-reports.component.html',
  styleUrl: './chemical-reports.component.scss'
})
export class ChemicalReportsComponent implements OnInit {
  loading = false;
  exportLoading = false;
  errorMessage = '';
  successMessage = '';

  // Filters
  filterStartDate = '';
  filterEndDate = '';
  filterOrganization = '';
  filterChemical = '';
  filterManufacturer = '';
  unitUsed: UnitSelection = 'original';
  volumeUnits = VOLUME_UNITS;
  reportView: 'by-organization' | 'by-chemical' = 'by-organization';

  // Organizations for dropdown
  organizations: OrganizationOption[] = [];

  // Chemical filter options
  chemicals: ChemicalOption[] = [];
  manufacturers: string[] = [];

  // Report data
  orgDetailData: ChemicalReportOrgDetail | null = null;
  summaryData: ChemicalReportSummary | null = null;
  aggregateData: ChemicalReportAggregate | null = null;

  constructor(
    private chemicalReportService: ChemicalReportService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit(): void {
    this.initializeDateRange();
    this.loadOrganizations();
    this.loadFilterOptions();
  }

  /**
   * Initialize date range to current month (first and last day)
   */
  private initializeDateRange(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.filterStartDate = this.formatDateForInput(firstDay);
    this.filterEndDate = this.formatDateForInput(lastDay);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Load organizations for filter dropdown
   */
  async loadOrganizations(): Promise<void> {
    try {
      const page = await firstValueFrom(this.organizationService.getOptions());
      this.organizations = page.items;
    } catch (error: unknown) {
      console.error('Failed to load organizations:', error);
    }
  }

  /**
   * Load filter options (chemicals and manufacturers)
   */
  async loadFilterOptions(): Promise<void> {
    try {
      const options = await this.chemicalReportService.getFilters();
      this.chemicals = options.chemicals;
      this.manufacturers = options.manufacturers;
    } catch (error: unknown) {
      console.error('Failed to load filter options:', error);
    }
  }

  /**
   * Generate report based on current filters
   */
  generateReport(): void {
    this.loadReportData();
  }

  /**
   * Load report data based on organization selection
   */
  async loadReportData(): Promise<void> {
    if (!this.filterStartDate || !this.filterEndDate) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.orgDetailData = null;
    this.summaryData = null;
    this.aggregateData = null;

    try {
      if (this.filterOrganization) {
        this.orgDetailData = await this.chemicalReportService.getOrganizationDetail(
          this.filterOrganization,
          this.filterStartDate,
          this.filterEndDate,
          this.filterChemical || undefined
        );
      } else if (this.reportView === 'by-chemical') {
        this.aggregateData = await this.chemicalReportService.getChemicalAggregate(
          this.filterStartDate,
          this.filterEndDate,
          this.filterManufacturer || undefined
        );
      } else {
        this.summaryData = await this.chemicalReportService.getSummary(
          this.filterStartDate,
          this.filterEndDate,
          this.filterManufacturer || undefined
        );
      }
    } catch (error: unknown) {
      console.error('Failed to load report data:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to load report data';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Export current report to PDF
   */
  async exportToPdf(): Promise<void> {
    this.exportLoading = true;
    this.errorMessage = '';

    try {
      const filters = {
        startDate: this.filterStartDate,
        endDate: this.filterEndDate,
        organizationId: this.filterOrganization,
        chemicalId: this.filterChemical,
        manufacturer: this.filterManufacturer
      };

      let reportMode: 'organization-detail' | 'cross-organization-summary' | 'chemical-aggregate';
      let rawData: any;

      if (this.filterOrganization) {
        reportMode = 'organization-detail';
        rawData = this.orgDetailData;
      } else if (this.reportView === 'by-chemical') {
        reportMode = 'chemical-aggregate';
        rawData = this.aggregateData;
      } else {
        reportMode = 'cross-organization-summary';
        rawData = this.summaryData;
      }

      if (!rawData) {
        this.errorMessage = 'No data to export';
        this.exportLoading = false;
        return;
      }

      // Pre-convert amounts so the PDF mirrors what's on screen. Hub template
      // renders raw values — no unit conversion happens server-side.
      const convertedData = this.convertDataForExport(rawData, reportMode);

      const data = {
        ...convertedData,
        isSummaryMode: reportMode === 'cross-organization-summary',
        isAggregateMode: reportMode === 'chemical-aggregate',
      };

      const result = await this.chemicalReportService.exportPdf(
        reportMode,
        filters,
        data
      );

      if (result && result.downloadUrl) {
        openExternal(result.downloadUrl);
        this.successMessage = 'PDF generated successfully';
        this.clearSuccessMessage();
      }
    } catch (error: unknown) {
      console.error('PDF export error:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to export PDF';
    } finally {
      this.exportLoading = false;
    }
  }

  /**
   * Clear success message after delay
   */
  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const str = String(dateString);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const date = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    const date = new Date(str);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /**
   * Format number with commas
   */
  formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  /**
   * Walk the report data and convert every chemical's totalAmountUsed to the
   * currently selected display unit. Hub PDF template renders raw values, so we
   * pre-convert here to mirror what the UI shows. When conversion fails, both
   * the value and the unit are kept as-is to avoid mislabeled rows.
   */
  private convertDataForExport(
    data: any,
    mode: 'organization-detail' | 'cross-organization-summary' | 'chemical-aggregate'
  ): any {
    const cloned = JSON.parse(JSON.stringify(data));

    const convertChemical = (chem: any) => {
      if (chem == null) return;
      if (this.unitUsed === 'original') return;
      const converted = convertVolume(chem.totalAmountUsed, chem.unit, this.unitUsed);
      if (converted == null) return;
      chem.totalAmountUsed = converted;
      chem.unit = this.unitUsed;
    };

    if (mode === 'organization-detail') {
      cloned.chemicalUsage?.forEach(convertChemical);
    } else if (mode === 'cross-organization-summary') {
      cloned.organizations?.forEach((org: any) => org.chemicals?.forEach(convertChemical));
    } else if (mode === 'chemical-aggregate') {
      cloned.chemicals?.forEach(convertChemical);
    }
    return cloned;
  }


  /**
   * Get organization name by ID
   */
  getOrganizationName(organizationId: string): string {
    const org = this.organizations.find(o => o.id === organizationId);
    return org?.name || organizationId;
  }

  /**
   * Get chemical name by ID
   */
  getChemicalName(chemicalId: string): string {
    const chemical = this.chemicals.find(c => c.id === chemicalId);
    return chemical?.name || chemicalId;
  }
}
